// hooks/useStickyHeaders.ts
import { useState, useEffect, useRef, useCallback, RefObject } from "react";
import { throttle } from "lodash-es";

type HeaderInfo = {
  id: string;
  offset: number; // 相对容器的偏移量
};

type Config = {
  containerRef: RefObject<HTMLElement>;
  headerSelector?: string;
  offset?: number; // 可选的额外偏移量（如顶部导航栏高度）
};

export const useStickyHeaders = ({
  containerRef,
  headerSelector = ".data-sticky-header",
  offset = 0,
}: Config) => {
  const [currentStickyHeader, setCurrentStickyHeader] = useState<string | null>(
    null,
  );
  const headersRef = useRef<HTMLElement[]>([]);
  const rafId = useRef<any>(null);

  // 获取容器位置和滚动状态
  const getContainerScroll = useCallback(() => {
    return containerRef.current?.scrollTop || 0;
  }, [containerRef]);

  // 动态计算 Header 相对容器的位置
  const updateHeaderPositions = useCallback(() => {
    if (!containerRef.current) {
      return [];
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const headers = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(headerSelector),
    );
    headersRef.current = headers;

    const headerInfos: HeaderInfo[] = headers.map((header) => {
      const headerRect = header.getBoundingClientRect();
      return {
        id: header.dataset.id || "",
        offset: headerRect.top - containerRect.top + getContainerScroll(),
      };
    });

    return headerInfos.sort((a, b) => a.offset - b.offset);
  }, [containerRef, headerSelector, getContainerScroll]);

  // 判断当前吸顶 Header
  const checkSticky = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const scrollTop = getContainerScroll();
    const sortedHeaders = updateHeaderPositions();

    let currentId: string | null = null;
    for (const header of sortedHeaders) {
      if (header.offset <= scrollTop + offset + 1) {
        // +1 防抖动
        currentId = header.id;
      } else {
        break;
      }
    }
    setCurrentStickyHeader(currentId);
  }, [updateHeaderPositions, getContainerScroll, offset, containerRef]);

  // 优化滚动监听
  const handleScroll = useCallback(() => {
    if (rafId.current) {
      return;
    }
    rafId.current = requestAnimationFrame(() => {
      checkSticky();
      rafId.current = undefined;
    });
  }, [checkSticky]);

  // 监听容器变化
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    // 初始化计算
    checkSticky();

    const throttledHandleScroll = throttle(handleScroll, 100);

    // 监听容器滚动
    container.addEventListener("scroll", throttledHandleScroll);

    // 监听容器尺寸变化
    const resizeObserver = new ResizeObserver(throttledHandleScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", throttledHandleScroll);
      resizeObserver.unobserve(container);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [handleScroll, checkSticky, containerRef]);

  return { currentStickyHeader };
};
